// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
// biome-ignore lint/style/useImportType: <explanation>
import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '../../lib/base';
import type { DeleteProductMutation, DeleteProductMutationVariables } from '../../types/__generated__/graphql';
import { ExitDialogButton } from '../common/button';

const DELETE_PRODUCT_MUT = gql`
  mutation DeleteProduct($product_id: Int!) {
    deleteProduct(input: { product_id: $product_id }) {
      updated_crop {
        id
        updated_at
      }
      updated_blend {
        id
        updated_at
      }
    }
  }
`;

interface Props {
  readonly productId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetch: boolean) => void;
}

export default function DeleteProductDialog({ isOpen, onClose, onSuccess, productId }: Props) {
  const [delProduct, { loading }] = useMutation<DeleteProductMutation, DeleteProductMutationVariables>(
    DELETE_PRODUCT_MUT,
  );

  const disableFields = loading;

  const onSubmit = () => {
    void delProduct({
      variables: {
        product_id: productId,
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.deleteProduct != null) {
          enqueueSnackbar('Deleted product', { variant: 'success' });
          onSuccess(true);
        } else {
          enqueueSnackbar('Error deleting product', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
      },
    });
  };

  const hasInputValidationError: boolean = false;
  const requiredFieldsFilled: boolean = true;
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields || loading;

  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="sm"
    >
      <DialogContent sx={{}}>
        <ExitDialogButton onCloseDialog={onClose} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ mb: 1 }} variant="h4">
            Delete Product
          </Typography>
          <Typography variant="body1">
            Are you sure you want to delete this product? This action cannot be undone.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
